import { Profile } from '../types/profile';

const PROFILE_SNAPSHOT_KEY = 'hp_profile_snapshot';

/**
 * Minimum profile data needed to paint the logged-in shell on public routes
 * (TopBar avatar, HomePage chef-vs-consumer branches, ListingDetailsPage
 * connection-level check) before whoami's `getProfileById` round-trip
 * (~600ms) returns.
 *
 * Intentionally narrow: anything beyond these fields requires the full
 * `activeProfile`. That boundary is enforced by `useDisplayProfile`'s
 * return type. If a new public-path UI ever needs another field, the team
 * adds it here knowingly (and accepts that it'll be cached client-side)
 * rather than silently widening the cache surface.
 *
 * Shape mirrors the nested path in `Profile` so structural helpers like
 * `isChef` accept both `Profile` and `ProfileSnapshot` without a separate
 * code path.
 */
export interface ProfileSnapshot {
  id: string;
  displayName: string;
  avatarUrl?: string;
  invitedById?: string;
  metadata: {
    onboarding?: {
      selectedRole?: string;
    };
  };
}

export function profileToSnapshot(profile: Profile): ProfileSnapshot {
  return {
    id: profile.id,
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
    invitedById: profile.invitedById || undefined,
    metadata: {
      onboarding: profile.metadata?.onboarding?.selectedRole
        ? { selectedRole: profile.metadata.onboarding.selectedRole }
        : undefined,
    },
  };
}

export function loadProfileSnapshot(): ProfileSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PROFILE_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ProfileSnapshot> & {
      metadata?: Partial<ProfileSnapshot['metadata']>;
    };
    if (typeof parsed.id !== 'string' || typeof parsed.displayName !== 'string') {
      return null;
    }
    const selectedRole = parsed.metadata?.onboarding?.selectedRole;
    return {
      id: parsed.id,
      displayName: parsed.displayName,
      avatarUrl: typeof parsed.avatarUrl === 'string' ? parsed.avatarUrl : undefined,
      invitedById: typeof parsed.invitedById === 'string' ? parsed.invitedById : undefined,
      metadata: {
        onboarding: typeof selectedRole === 'string' ? { selectedRole } : undefined,
      },
    };
  } catch {
    return null;
  }
}

export function saveProfileSnapshot(snap: ProfileSnapshot): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PROFILE_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    // Quota exceeded / disabled — degrade silently. Worst case: this user's
    // next cold boot flickers like before.
  }
}

export function clearProfileSnapshot(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(PROFILE_SNAPSHOT_KEY);
}
